// Renders the AI Insights + AI Recommendations sections that appear at the
// end of every page in the rich report.
import { safeText } from './utils';

export function renderInsights(doc, y, insights, recommendations) {
  const pw = doc.internal.pageSize.getWidth();

  // Section 1: AI Insights — always rendered if available
  if (insights && insights.length > 0) {
    if (y > 230) { doc.addPage(); y = 15; }

    doc.setFontSize(10);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(102, 51, 153); // purple
    doc.text('AI Insights', 14, y);
    y += 6;

    insights.forEach((insight) => {
      if (y > 270) { doc.addPage(); y = 15; }
      doc.setFillColor(102, 51, 153);
      doc.circle(17, y, 1.2, 'F');

      doc.setFontSize(8);
      doc.setFont('Helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(safeText(insight.title || 'Observation'), 21, y + 1);
      y += 4;

      doc.setFontSize(7);
      doc.setFont('Helvetica', 'normal');
      doc.setTextColor(60);
      const lines = doc.splitTextToSize(safeText(insight.description || ''), pw - 40);
      doc.text(lines, 21, y);
      y += lines.length * 3 + 4;
    });
  }

  // Section 2: AI Recommendations — premium only, rendered only if present
  if (recommendations && recommendations.length > 0) {
    // Only break to a new page if there isn't enough room for header + first rec.
    if (y > 255) { doc.addPage(); y = 15; }
    else y += 4;

    doc.setFontSize(10);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(180, 120, 0); // amber
    doc.text('AI Recommendations', 14, y);
    y += 6;

    recommendations.forEach((rec) => {
      if (y > 260) { doc.addPage(); y = 15; }
      const pColor = rec.priority === 'high' ? [220, 38, 38]
        : rec.priority === 'medium' ? [217, 119, 6]
          : [22, 163, 74];
      doc.setFillColor(...pColor);
      doc.circle(17, y, 1.5, 'F');

      // Title
      doc.setFontSize(8);
      doc.setFont('Helvetica', 'bold');
      doc.setTextColor(0);
      const titleText = safeText(rec.title || 'Recommendation');
      doc.text(titleText, 21, y + 1);
      let badgeX = 21 + doc.getTextWidth(titleText) + 3;

      // Timeline badge
      if (rec.timeline) {
        const tLabel = safeText(rec.timeline);
        const tColor = rec.timeline === 'Quick Win' ? [22, 163, 74]
          : rec.timeline === 'Medium-term' ? [217, 119, 6]
            : [79, 70, 229];
        const tW = doc.getTextWidth(tLabel) + 4;
        doc.setFillColor(...tColor);
        doc.roundedRect(badgeX, y - 2.5, tW, 5, 1, 1, 'F');
        doc.setFontSize(6);
        doc.setFont('Helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(tLabel, badgeX + 2, y + 0.8);
        badgeX += tW + 3;
      }

      // Category badge
      if (rec.category) {
        const cLabel = safeText(rec.category.replace(/_/g, ' '));
        const catColors = {
          cost_savings: [16, 124, 65],
          risk_mitigation: [180, 83, 9],
          consolidation: [37, 99, 235],
          compliance: [109, 40, 217],
          strategic: [14, 116, 144],
        };
        const cColor = catColors[rec.category] || [100, 100, 100];
        const cW = doc.getTextWidth(cLabel) + 4;
        doc.setDrawColor(...cColor);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(badgeX, y - 2.5, cW, 5, 1, 1, 'FD');
        doc.setFontSize(6);
        doc.setFont('Helvetica', 'normal');
        doc.setTextColor(...cColor);
        doc.text(cLabel, badgeX + 2, y + 0.8);
      }

      y += 5;

      doc.setFontSize(7);
      doc.setFont('Helvetica', 'normal');
      doc.setTextColor(60);
      const lines = doc.splitTextToSize(safeText(rec.description || ''), pw - 40);
      doc.text(lines, 21, y);
      y += lines.length * 3 + 1;

      if (rec.potential_impact) {
        doc.setFontSize(6.5);
        doc.setTextColor(22, 163, 74);
        doc.text(safeText(`Impact: ${rec.potential_impact}`), 21, y);
        y += 4;
      }
      y += 3;
    });
  }

  // Disclaimer footer
  if ((insights?.length > 0 || recommendations?.length > 0)) {
    if (y > 260) { doc.addPage(); y = 15; }
    y += 3;
    doc.setDrawColor(180, 180, 180);
    doc.line(14, y, pw - 14, y);
    y += 4;
    doc.setFontSize(5.5);
    doc.setFont('Helvetica', 'italic');
    doc.setTextColor(120);
    const disclaimer = 'Note: Insights and recommendations in this report are generated from the data provided by your organisation. The quality and accuracy of your source data has a direct impact on the analysis, insights, and recommendations produced. Savings estimates and projections are indicative - actual outcomes will depend on the depth of due diligence, quality of documentation, strength of governance, and effectiveness of planning and execution.';
    const discLines = doc.splitTextToSize(disclaimer, pw - 28);
    doc.text(discLines, 14, y);
    y += discLines.length * 2.5 + 3;
  }

  return y;
}
